<template>
  <public-layout
    badge="Get in Touch"
    title="Contact Us"
    subtitle="Have a question, feedback, or just want to say hello? We'd love to hear from you."
  >
    <!-- ═══════ CONTACT INFO CARDS ═══════ -->
    <section class="sc-section sc-contact-info">
      <div class="sc-container">
        <div class="contact-cards">
          <div class="contact-card">
            <div class="contact-card__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            </div>
            <h3>Email Us</h3>
            <div class="contact-card__details">
              <a v-if="company.email" :href="'mailto:' + company.email">{{ company.email }}</a>
              <a v-if="company.email2" :href="'mailto:' + company.email2">{{ company.email2 }}</a>
              <span v-if="!company.email && !company.email2" class="placeholder-text">Loading…</span>
            </div>
          </div>
          <div class="contact-card">
            <div class="contact-card__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.27 9.81 19.79 19.79 0 01.22 1.18 2 2 0 012.22 0h3a2 2 0 012 1.72c.13 1.03.37 2.04.72 3.01a2 2 0 01-.45 2.11L6.91 8.31a16 16 0 006.61 6.61l1.47-1.47a2 2 0 012.11-.45c.97.35 1.98.59 3.01.72A2 2 0 0122 16.92z"/></svg>
            </div>
            <h3>Call Us</h3>
            <div class="contact-card__details">
              <a v-if="company.tel" :href="'tel:' + company.tel">{{ company.tel }}</a>
              <a v-if="company.tel2" :href="'tel:' + company.tel2">{{ company.tel2 }}</a>
              <span v-if="!company.tel && !company.tel2" class="placeholder-text">Loading…</span>
            </div>
          </div>
          <div class="contact-card">
            <div class="contact-card__icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <h3>Our Address</h3>
            <div class="contact-card__details">
              <span v-if="company.address">{{ company.address }}</span>
              <span v-else class="placeholder-text">Loading…</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════ FORM + MAP ═══════ -->
    <section class="sc-section sc-contact-main">
      <div class="sc-container">
        <div class="contact-main-grid">
          <!-- Map -->
          <div class="contact-map">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d31713.548585611723!2d3.335719718941653!3d6.497160239459186!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x103b922773306ab9%3A0xc5efdb2add32d7e6!2sStructurecity!5e0!3m2!1sen!2sng!4v1668281032388!5m2!1sen!2sng"
              width="100%" height="100%" style="border:0; min-height:400px;" allowfullscreen loading="lazy"
              referrerpolicy="no-referrer-when-downgrade">
            </iframe>
          </div>

          <!-- Form -->
          <div class="contact-form-wrap">
            <div class="contact-form-header">
              <h2>Send us a message</h2>
              <p>We'll get back to you within 1–2 business days.</p>
            </div>

            <div v-if="success" class="sc-alert sc-alert--success">
              Message sent! We'll be in touch shortly.
            </div>
            <div v-if="errorMsg" class="sc-alert sc-alert--error">{{ errorMsg }}</div>

            <form v-if="!success" id="contact-form" @submit.prevent="submitContact">
              <div class="sc-field">
                <label>Your Name <span class="req">*</span></label>
                <input name="name" type="text" placeholder="Jane Doe" required />
              </div>
              <div class="sc-field-row">
                <div class="sc-field">
                  <label>Email Address <span class="req">*</span></label>
                  <input name="email" type="email" placeholder="you@example.com" required />
                </div>
                <div class="sc-field">
                  <label>Phone Number</label>
                  <input name="phone_number" type="tel" placeholder="+234 000 000 0000" />
                </div>
              </div>
              <div class="sc-field">
                <label>Message <span class="req">*</span></label>
                <textarea name="message" rows="5" placeholder="How can we help you?" required style="width:100%;padding:.625rem .875rem;border:1.5px solid var(--border);border-radius:var(--radius-sm);font-family:var(--font);font-size:.9375rem;color:var(--text);background:var(--surface);outline:none;resize:vertical;transition:border-color .15s,box-shadow .15s;"></textarea>
              </div>
              <button type="submit" class="btn-primary" style="width:100%;justify-content:center;padding:.75rem;font-size:1rem;" :disabled="loading">
                <span v-if="loading" class="sc-spinner"></span>
                <span v-else>Send Message</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  </public-layout>
</template>

<script>
import axios from 'axios';
import PublicLayout from '@/layouts/PublicLayout.vue';
import { useSEO, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'Contact',
  components: { PublicLayout },

  data() {
    return {
      company: {},
      loading: false,
      success: false,
      errorMsg: '',
    };
  },

  created() {
    useSEO({
      title: 'Contact Us',
      description: "Get in touch with the Structurecity team. We're here to help with your construction and real estate questions.",
      url: SITE_ORIGIN + '/contact',
    });
    this.fetchCompany();
  },

  methods: {
    fetchCompany() {
      axios(process.env.VUE_APP_API_URL + 'company')
        .then(res => { if (res.status === 200) this.company = res.data || {}; })
        .catch(() => {});
    },

    submitContact() {
      this.loading = true; this.errorMsg = '';
      const data = new FormData(document.getElementById('contact-form'));
      axios.post(process.env.VUE_APP_API_URL + 'contact', data)
        .then(res => {
          if (res.status === 200) {
            this.success = true;
          } else {
            this.errorMsg = res.message || 'Something went wrong. Please try again.';
          }
        })
        .catch(() => { this.errorMsg = 'Could not send your message. Please try again.'; })
        .finally(() => { this.loading = false; });
    },
  },
};
</script>

<style scoped>
.sc-contact-info { background: var(--surface); padding-bottom: 0; }
.sc-contact-main { background: var(--surface-2); }

/* Info cards */
.contact-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem; }
.contact-card {
  background: var(--surface); border-radius: var(--radius-lg);
  padding: 2rem 1.75rem; border: 1px solid var(--border-light); box-shadow: var(--shadow-sm);
  text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1rem;
  transition: transform var(--transition), box-shadow var(--transition);
}
.contact-card:hover { transform: translateY(-3px); box-shadow: var(--shadow-lg); }
.contact-card__icon {
  width: 52px; height: 52px; border-radius: 50%;
  background: rgba(30,58,95,.08); color: var(--primary);
  display: flex; align-items: center; justify-content: center;
}
.contact-card h3 { font-size: 1.0625rem; font-weight: 700; color: var(--text); margin: 0; }
.contact-card__details { display: flex; flex-direction: column; gap: .375rem; }
.contact-card__details a { color: var(--primary); font-size: .9375rem; text-decoration: none; font-weight: 500; }
.contact-card__details a:hover { text-decoration: underline; }
.contact-card__details span { color: var(--text-muted); font-size: .9375rem; }
.placeholder-text { color: var(--text-light); }

/* Form + map */
.contact-main-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; align-items: start; }
.contact-map { border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-lg); height: 100%; min-height: 400px; }
.contact-form-wrap { background: var(--surface); border-radius: var(--radius-lg); padding: 2.5rem; box-shadow: var(--shadow); }
.contact-form-header { margin-bottom: 1.75rem; }
.contact-form-header h2 { font-size: 1.625rem; font-weight: 800; color: var(--text); margin: 0 0 .375rem; letter-spacing: -.02em; }
.contact-form-header p { color: var(--text-muted); margin: 0; }
.req { color: var(--danger); }

.sc-field { margin-bottom: 1rem; }
.sc-field label { display: block; font-size: .875rem; font-weight: 500; color: var(--text); margin-bottom: .375rem; }
.sc-field input {
  width: 100%; padding: .625rem .875rem;
  border: 1.5px solid var(--border); border-radius: var(--radius-sm);
  font-family: var(--font); font-size: .9375rem; color: var(--text);
  background: var(--surface); outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.sc-field input:focus { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(30,58,95,.1); }
.sc-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: .75rem; margin-bottom: 0; }
.sc-alert { padding: .75rem 1rem; border-radius: var(--radius-sm); font-size: .875rem; margin-bottom: 1rem; }
.sc-alert--error   { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.sc-alert--success { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
.sc-spinner { display: inline-block; width: 18px; height: 18px; border: 2px solid rgba(0,0,0,.15); border-top-color: currentColor; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 900px) {
  .contact-cards { grid-template-columns: 1fr; }
  .contact-main-grid { grid-template-columns: 1fr; }
  .contact-map { min-height: 300px; }
  .sc-field-row { grid-template-columns: 1fr; }
}
</style>
